/* oxlint-disable no-inner-declarations */
import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';

import {
  type TaskDetails,
  type TaskUpdate,
} from '@/task-manager/entities/task-details';
import type { TaskPreview } from '@/task-manager/entities/task-preview';
import { Optimistic } from '@/utils/react-query/optimistic';

import { type BodyOf, tasksV1CardsUpdateUpdate } from '../fetch-client';
import { GetTask } from '../queries/get-task';
import { ListTasks } from '../queries/list-tasks';
import { useFetchOpts } from '../use-fetch-opts';

export namespace UpdateTask {
  export type Params = { onSuccess?: () => Promise<void> | void };
  export type Input = TaskUpdate;
  export type Output = void;

  function bodyFromInput(
    input: Input,
  ): BodyOf<typeof tasksV1CardsUpdateUpdate> {
    return {
      uuid: input.id,
      updateCardViewInputRequest: {
        title: input.title,
        subtitle: input.subtitle,
        description: input.description,
        status: input.status,
        priority: input.priority,
        frontend_level: input.device?.frontendLevel,
        frontend_id: input.device?.fullFrontendId,
        due_date: input.dueAt === null ? null : input.dueAt?.toISOString(),
        assignee_name: input.assignee === null ? '' : input.assignee?.name,
        assignee_email: input.assignee === null ? '' : input.assignee?.email,
        assignee_company:
          input.assignee === null ? '' : input.assignee?.company,
      },
    };
  }

  export function useMutation(params?: Params) {
    const getOpts = useFetchOpts();
    const queryClient = rq.useQueryClient();
    const [listTasksFilters] = ListTasks.useGetFiltersFromQueryParams();

    return rq.useMutation<Output, unknown, Input>({
      async mutationFn(input) {
        const opts = await getOpts();
        const body = bodyFromInput(input);
        await ok(tasksV1CardsUpdateUpdate(body, opts));
      },
      onMutate({ id, ...task }) {
        queryClient.cancelQueries(GetTask.key({ id }));
        queryClient.setQueryData(
          GetTask.key({ id }),
          GetTask.optimistic((previous) => ({
            ...previous,
            task: Optimistic.updateEntity<TaskDetails>(previous.task, task),
          })),
        );

        queryClient.cancelQueries(ListTasks.key({ filters: listTasksFilters }));
        queryClient.setQueryData(
          ListTasks.key({ filters: listTasksFilters }),
          ListTasks.optimistic((previous) => ({
            ...previous,
            tasks: Optimistic.updateItem<TaskPreview>(
              previous.tasks,
              (t) => t.id === id,
              task,
            ),
          })),
        );
      },
      async onSuccess() {
        await params?.onSuccess?.();
      },
      async onSettled(_, __, { id }) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: GetTask.key({ id }),
          }),
          queryClient.invalidateQueries({
            queryKey: ListTasks.key({
              filters: listTasksFilters,
            }),
          }),
          queryClient.invalidateQueries({
            queryKey: ['project-management'],
          }),
        ]);
      },
    });
  }
}
